import { flow, split, takeRight } from 'lodash/fp';
import Cookies from 'universal-cookie';
import { isGdprCookiePermited } from 'helpers/gdprPreferences';

const MILESECONDS_IN_ONE_DAY = 86400000;
const DOMAIN_EXTRACTOR_INDEX = 2;

const getCrossDomain = flow(
  split('.'),
  takeRight(DOMAIN_EXTRACTOR_INDEX),
  (result) => (result.length > 1 ? `.${result.join('.')}` : result.join('.')),
);

function getAll(rawCookies) {
  const cookies = new Cookies(rawCookies);

  return cookies.getAll();
}
/**
 * @typedef {Object} CookieService
 * @property {(key: string?) => string} get
 * @property {(key: string, value: any, expiresIn?: number, options?: { crossDomain?: boolean, expiresInDays?: boolean }) => void} set
 * @property {() => object} getAll
 * @property {(key: string) => string} remove
 */

/**
 * @param {Express.Request} req
 * @param {Express.Response} res
 * @returns {CookieService}
 */
const serverAPI = (req, res) => ({
  set: (
    name,
    value,
    expiresIn,
    { crossDomain, httpOnly, expiresInDays = true } = {},
  ) => {
    let cookieOptions = {};

    // Set Domain
    if (crossDomain) {
      cookieOptions = {
        ...cookieOptions,
        domain: getCrossDomain(req.hostname),
      };
    }

    // HTTP Cookie
    if (httpOnly) {
      cookieOptions = { ...cookieOptions, httpOnly };
    }

    if (expiresInDays) {
      expiresIn *= MILESECONDS_IN_ONE_DAY;
    }

    isGdprCookiePermited(
      getAll(req.cookies),
      name,
      res._GDPR_BANNER_ENABLED,
    ).then((isPermitted) => {
      if (isPermitted) {
        res.cookie(name, value, {
          expires: new Date(Date.now() + expiresIn),
          ...cookieOptions,
        });
      }
    });
  },

  get: (name) => {
    if (typeof name !== 'undefined') {
      return req.cookies[name];
    }

    return getAll(req.get('cookie'));
  },

  /**
   * Returns cookies as key/value object map.
   * @returns {object} Cookies
   */
  getAll: () => getAll(req.get('cookie')),

  remove: (name) => res.clearCookie(name),
});

/**
 * @returns {CookieService}
 */
const clientAPI = () => ({
  set: (name, value, expiresIn, { crossDomain, expiresInDays = true } = {}) => {
    let domain = '';

    let expires = '';

    const cookieService = new Cookies(document.cookie);

    if (expiresIn) {
      if (expiresInDays) {
        expiresIn *= MILESECONDS_IN_ONE_DAY;
      }

      const date = new Date();

      date.setTime(date.getTime() + expiresIn);

      expires = `; expires=${date.toGMTString()}`;
    }

    if (crossDomain) {
      const { hostname } = window.location;

      domain = `; domain=${getCrossDomain(hostname)}`;
    }

    if (typeof value === 'object') {
      value = JSON.stringify(value);
    }

    isGdprCookiePermited(cookieService.getAll(), name).then((isPermitted) => {
      if (isPermitted) {
        document.cookie = `${name}=${value}${domain}${expires}; path=/`;
      }
    });
  },

  get: (name) => {
    if (!name) {
      return getAll(document.cookie);
    }

    // eslint-disable-next-line
    name = name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1');

    const matches = document.cookie.match(
      new RegExp(`(?:^|; )${name}=([^;]*)`),
    );

    return matches ? decodeURIComponent(matches[1]) : null;
  },

  /**
   * Returns cookies as key/value object map.
   * @returns {object} Cookies
   */
  getAll: () => getAll(document.cookie),

  remove: (name) => {
    document.cookie = `${name}=; max-age=-1`;
  },
});

export default (...args) => {
  return __SERVER__ ? serverAPI(...args) : clientAPI(...args);
};
